import axios from "axios";
import api from "./api";

/**
 * Helper to extract meaningful error message
 */
const getErrorMessage = (error, defaultMsg) => {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    defaultMsg
  );
};

/* ==========================================================================
   PREREQUISITES & COMMON S3 SERVICES
   ========================================================================== */

/**
 * Get S3 presigned PUT URL for prerequisite file upload
 * GET /event_uploads/prerequisites/presigned-upload-url?file_name=<file_name>
 */
export const getPrerequisitePresignedUploadUrl = async (fileName) => {
  try {
    const res = await api.get("/event_uploads/prerequisites/presigned-upload-url", {
      params: { file_name: fileName }
    });
    return res.data; // { presigned_url, key_name }
  } catch (error) {
    console.error("Error getting presigned upload URL:", error);
    throw new Error(getErrorMessage(error, "Failed to get presigned upload URL"));
  }
};

/**
 * Upload binary file directly to S3 using presigned PUT URL
 * NOTE: Use standard axios without Bearer token headers for AWS S3
 */
export const uploadFileToS3 = async (presignedUrl, file, onProgress) => {
  try {
    return await axios.put(presignedUrl, file, {
      headers: {
        "Content-Type": file.type || "application/octet-stream"
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      }
    });
  } catch (error) {
    console.error("Error uploading file directly to S3:", error);
    throw new Error(getErrorMessage(error, "Failed to upload file to S3"));
  }
};

/**
 * Create EventPrerequisite for a domain
 * POST /event_uploads/event-prerequisites
 * Body: { domain_id: number, meta_data: object, uploads: array }
 */
export const createEventPrerequisite = async ({ domain_id, meta_data, uploads = [] }) => {
  try {
    const res = await api.post("/event_uploads/event-prerequisites", {
      domain_id,
      meta_data,
      uploads,
    });
    return res.data;
  } catch (error) {
    console.error("Error creating event prerequisite:", error);
    throw new Error(getErrorMessage(error, "Failed to save event prerequisites"));
  }
};

/**
 * Update existing EventPrerequisite for a domain
 * PATCH /event_uploads/event-prerequisites/{prerequisite_id}
 * Body: { domain_id?: number, meta_data?: object, uploads?: array }
 */
export const updateEventPrerequisite = async (prerequisiteId, { domain_id, meta_data, uploads }) => {
  try {
    const payload = {};
    if (domain_id !== undefined) payload.domain_id = domain_id;
    if (meta_data !== undefined) payload.meta_data = meta_data;
    if (uploads !== undefined) payload.uploads = uploads;

    const res = await api.patch(`/event_uploads/event-prerequisites/${prerequisiteId}`, payload);
    return res.data;
  } catch (error) {
    console.error(`Error updating event prerequisite ${prerequisiteId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to update event prerequisites"));
  }
};

/**
 * Delete a single EventPrerequisiteUpload row and remove its file from S3
 * DELETE /event_uploads/event-prerequisite-upload-delete/{upload_id}
 */
export const deleteEventPrerequisiteUpload = async (uploadId) => {
  try {
    const res = await api.delete(`/event_uploads/event-prerequisite-upload-delete/${uploadId}`);
    return res.data;
  } catch (error) {
    console.error(`Error deleting event prerequisite upload ${uploadId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to delete prerequisite attachment"));
  }
};


/**
 * Fetch EventPrerequisite records for a domain
 * GET /event_uploads/event-prerequisites?domain_id=<domain_id>
 */
export const fetchEventPrerequisitesByDomain = async (domainId) => {
  if (!domainId) return [];
  try {
    const res = await api.get("/event_uploads/event-prerequisites", {
      params: { domain_id: domainId },
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error(`Error fetching event prerequisites for domain ${domainId}:`, error);
    return [];
  }
};

/**
 * Create or save prerequisite request with uploaded S3 keys
 * POST /event_uploads/prerequisites_requests
 * Body: { bookslot_id: number, files: [{ title, file_name, key_name }] }
 */
export const createPrerequisitesRequest = async ({ bookslot_id, files }) => {
  try {
    const res = await api.post("/event_uploads/prerequisites_requests", {
      bookslot_id,
      files
    });
    return res.data;
  } catch (error) {
    console.error("Error creating prerequisite request:", error);
    throw new Error(getErrorMessage(error, "Failed to submit prerequisite request"));
  }
};

/**
 * Fetch uploaded prerequisite files for a specific request ID
 * GET /event_uploads/prerequisites/{request_id}
 */
export const fetchPrerequisitesByRequestId = async (requestId) => {
  if (!requestId) return [];
  try {
    const res = await api.get(`/event_uploads/prerequisites/${requestId}`);
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error(`Error fetching prerequisites for request ${requestId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to fetch prerequisite files"));
  }
};

/**
 * Get S3 presigned GET URL for viewing (inline) or downloading (attachment) an image or file
 * GET /event_uploads/image-url?s3_key=<s3_key>&download=<bool>
 */
export const getFilePresignedViewUrl = async (s3Key, isDownload = false, fileName = null) => {
  if (!s3Key) return "";
  try {
    const params = { s3_key: s3Key };
    if (isDownload) {
      params.download = true;
      if (fileName) params.file_name = fileName;
    }
    const res = await api.get("/event_uploads/image-url", { params });
    return res.data?.url || "";
  } catch (error) {
    console.error(`Error getting view URL for key ${s3Key}:`, error);
    throw new Error(getErrorMessage(error, "Failed to get file view URL"));
  }
};

/**
 * Review (Approve or Reject) a prerequisite request (Admin side)
 * PATCH /event_uploads/prerequisites_requests/{request_id}/review
 * Body: { action: "approved" } or { action: "rejected", reason: "..." }
 */
export const reviewPrerequisitesRequest = async (requestId, payload) => {
  try {
    const res = await api.patch(
      `/event_uploads/prerequisites_requests/${requestId}/review`,
      payload
    );
    return res.data;
  } catch (error) {
    console.error(`Error reviewing prerequisite request ${requestId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to review prerequisite request"));
  }
};

/**
 * Fetch pending prerequisite requests for Admin
 * GET /event_uploads/admin/prerequisites_requests
 */
export const getAdminPendingPrerequisitesRequests = async () => {
  try {
    const res = await api.get("/event_uploads/admin/prerequisites_requests");
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error("Error fetching admin pending prerequisite requests:", error);
    throw new Error(getErrorMessage(error, "Failed to fetch admin prerequisite requests"));
  }
};

/**
 * Delete an object from S3
 * DELETE /event_uploads/s3/delete-object?key=<s3_key>
 */
export const deleteS3Object = async (s3Key) => {
  if (!s3Key) return null;
  try {
    const res = await api.delete("/event_uploads/s3/delete-object", {
      params: { key: s3Key }
    });
    return res.data;
  } catch (error) {
    console.error(`Error deleting S3 object with key ${s3Key}:`, error);
    return null; // Return null so callers aren't blocked on cleanup
  }
};

/**
 * Designer side — Delete a prerequisite file upload by upload_id
 * DELETE /event_uploads/designer/delete-prerequisite/{upload_id}
 */
export const deleteDesignerPrerequisiteUpload = async (uploadId) => {
  if (!uploadId) return null;
  try {
    const res = await api.delete(
      `/event_uploads/designer/delete-prerequisite/${uploadId}`
    );
    return res.data;
  } catch (error) {
    console.error(`Error deleting designer prerequisite upload ${uploadId}:`, error);
    throw new Error(
      getErrorMessage(error, "Failed to delete uploaded prerequisite file")
    );
  }
};

/* ==========================================================================
   INSTITUTE SIDE SERVICES
   ========================================================================== */

/**
 * Institute side — Fetch all active prerequisites for a given bookslot
 * GET /event_uploads/institute/prerequisites/{bookslot_id}
 */
export const fetchInstitutePrerequisites = async (bookslotId) => {
  if (!bookslotId) return { bookslot_id: null, request_id: null, status: null, files: [] };
  try {
    const res = await api.get(`/event_uploads/institute/prerequisites/${bookslotId}`);
    return res.data || { bookslot_id: bookslotId, request_id: null, status: null, files: [] };
  } catch (error) {
    console.error(`Error fetching institute prerequisites for bookslot ${bookslotId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to fetch institute prerequisites"));
  }
};

/**
 * Institute side — Get presigned GET URLs for all active prerequisite files
 * GET /event_uploads/institute/prerequisites/{bookslot_id}/download-all
 */
export const downloadAllInstitutePrerequisites = async (bookslotId) => {
  if (!bookslotId) return { bookslot_id: null, request_id: null, files: [] };
  try {
    const res = await api.get(`/event_uploads/institute/prerequisites/${bookslotId}/download-all`);
    return res.data || { bookslot_id: bookslotId, request_id: null, files: [] };
  } catch (error) {
    console.error(`Error downloading all institute prerequisites for bookslot ${bookslotId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to download all prerequisite files"));
  }
};

/**
 * Institute side — Get S3 presigned PUT URL for institute event photo upload
 * GET /event_uploads/institute_upload/presigned-upload-url?file_name=<file_name>
 */
export const getInstituteUploadPresignedUrl = async (fileName) => {
  try {
    const res = await api.get("/event_uploads/institute_upload/presigned-upload-url", {
      params: { file_name: fileName }
    });
    return res.data; // { presigned_url, key_name }
  } catch (error) {
    console.error("Error getting institute presigned upload URL:", error);
    throw new Error(getErrorMessage(error, "Failed to get presigned upload URL"));
  }
};

/**
 * Institute side — Create or submit event image uploads request for a bookslot
 * POST /event_uploads/institute/event-uploads
 * Body: { bookslot_id: number, files: [{ file_name, key_name }] }
 */
export const createInstituteEventUploadRequest = async ({ bookslot_id, files }) => {
  try {
    const res = await api.post("/event_uploads/institute/event-uploads", {
      bookslot_id,
      files
    });
    return res.data;
  } catch (error) {
    console.error("Error creating institute event upload request:", error);
    throw new Error(getErrorMessage(error, "Failed to submit event upload request"));
  }
};

/**
 * Institute side — Fetch all event uploads for a bookslot
 * GET /event_uploads/institute/event-uploads/{bookslot_id}
 */
export const fetchInstituteEventUploads = async (bookslotId) => {
  if (!bookslotId) return { bookslot_id: null, request_id: null, status: null, files: [] };
  try {
    const res = await api.get(`/event_uploads/institute/event-uploads/${bookslotId}`);
    return res.data || { bookslot_id: bookslotId, request_id: null, status: null, files: [] };
  } catch (error) {
    console.error(`Error fetching institute event uploads for bookslot ${bookslotId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to fetch event uploads"));
  }
};

/**
 * Institute side — Delete a single event upload file
 * DELETE /event_uploads/institute/delete-event-uploads/{upload_id}
 */
export const deleteInstituteEventUpload = async (uploadId) => {
  if (!uploadId) return null;
  try {
    const res = await api.delete(`/event_uploads/institute/delete-event-uploads/${uploadId}`);
    return res.data;
  } catch (error) {
    console.error(`Error deleting institute event upload ${uploadId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to delete uploaded event photo"));
  }
};

/* ==========================================================================
   COORDINATOR SIDE SERVICES
   ========================================================================== */

/**
 * Coordinator side — List EventUploadRequests (paginated & searched)
 * GET /event_uploads/coordinator/event-upload-requests/{event_type}?search={search}&page={page}&page_size={page_size}
 */
export const fetchCoordinatorEventUploadRequests = async ({
  eventType = "fdp",
  search = "",
  page = 1,
  page_size = 10
} = {}) => {
  try {
    const params = { page, page_size };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    const normalizedType = String(eventType || "fdp").toLowerCase().includes("tech")
      ? "tech_camp"
      : "fdp";
    const res = await api.get(`/event_uploads/coordinator/event-upload-requests/${normalizedType}`, {
      params
    });
    return res.data || { page: 1, page_size: 10, total: 0, total_pages: 0, data: [] };
  } catch (error) {
    console.error("Error fetching coordinator event upload requests:", error);
    throw new Error(getErrorMessage(error, "Failed to fetch event upload requests"));
  }
};

/**
 * Coordinator side — Get all active files for a specific event upload request
 * GET /event_uploads/coordinator/event-upload-requests/{request_id}/files
 */
export const fetchCoordinatorEventUploadFiles = async (requestId) => {
  if (!requestId) return { request_id: null, bookslot_id: null, status: null, files: [] };
  try {
    const res = await api.get(`/event_uploads/coordinator/event-upload-requests/${requestId}/files`);
    return res.data || { request_id: requestId, files: [] };
  } catch (error) {
    console.error(`Error fetching files for request ${requestId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to fetch request files"));
  }
};

/**
 * Coordinator side — Accept or reject an event upload request
 * PATCH /event_uploads/coordinator/event-upload-requests/{request_id}/review
 * Body: { "action": "accepted" } or { "action": "rejected", "reason": "..." }
 */
export const reviewCoordinatorEventUploadRequest = async (requestId, payload) => {
  try {
    const res = await api.patch(
      `/event_uploads/coordinator/event-upload-requests/${requestId}/review`,
      payload
    );
    return res.data;
  } catch (error) {
    console.error(`Error reviewing request ${requestId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to review event upload request"));
  }
};

/* ==========================================================================
   DESIGNER SIDE SERVICES
   ========================================================================== */

/**
 * Designer side — List accepted event upload bookslots for social media upload
 * GET /event_uploads/designer/accepted-event-upload-bookslots
 */
export const fetchDesignerSocialMediaUploadRequests = async ({
  search = "",
  page = 1,
  page_size = 10
} = {}) => {
  try {
    const params = { page, page_size };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    const res = await api.get("/event_uploads/designer/accepted-event-upload-bookslots", { params });
    return (
      res.data || {
        page: 1,
        page_size: 10,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
        data: []
      }
    );
  } catch (error) {
    console.error("Error fetching designer social media upload requests:", error);
    throw new Error(getErrorMessage(error, "Failed to fetch social media upload requests"));
  }
};

/**
 * Designer side — Get institute-uploaded event images for an event upload request
 * GET /event_uploads/designer/event-upload-institutes/{event_upload_request_id}/files
 */
export const fetchDesignerEventUploadFiles = async (eventUploadRequestId) => {
  if (!eventUploadRequestId) return { bookslot_id: null, event_upload_request_id: null, files: [] };
  try {
    const res = await api.get(`/event_uploads/designer/event-upload-institutes/${eventUploadRequestId}/files`);
    return res.data || { event_upload_request_id: eventUploadRequestId, files: [] };
  } catch (error) {
    console.error(`Error fetching event upload files for request ${eventUploadRequestId}:`, error);
    throw new Error(getErrorMessage(error, "Failed to fetch event upload files"));
  }
};

/**
 * Designer side — Get S3 presigned PUT URL for poster upload
 * GET /event_uploads/designer/social-media-uploads/presigned-upload-url?file_name=<file_name>
 */
export const getDesignerPosterPresignedUploadUrl = async (fileName) => {
  try {
    const res = await api.get(
      "/event_uploads/designer/social-media-uploads/presigned-upload-url",
      {
        params: { file_name: fileName }
      }
    );
    return res.data; // { presigned_url, key_name }
  } catch (error) {
    console.error("Error getting designer poster presigned upload URL:", error);
    throw new Error(getErrorMessage(error, "Failed to get presigned upload URL"));
  }
};

/**
 * Designer side — Save uploaded poster records to DB after direct S3 upload
 * POST /event_uploads/designer/social-media-uploads
 * Body: { bookslot_id: number, files: [{ file_name, key_name }] }
 */
export const createDesignerSocialMediaUpload = async ({ bookslot_id, files }) => {
  try {
    const res = await api.post("/event_uploads/designer/social-media-uploads", {
      bookslot_id,
      files
    });
    return res.data;
  } catch (error) {
    console.error("Error saving designer social media upload:", error);
    throw new Error(getErrorMessage(error, "Failed to save social media posters"));
  }
};

/**
 * Designer side — Get uploaded poster files for a specific social media request
 * GET /event_uploads/designer/event-upload-posters/{poster_request_id}/files
 */
export const fetchDesignerEventUploadPosters = async (posterRequestId) => {
  if (!posterRequestId) return { poster_request_id: null, files: [] };
  try {
    const res = await api.get(
      `/event_uploads/designer/event-upload-posters/${posterRequestId}/files`
    );
    return res.data || { poster_request_id: posterRequestId, files: [] };
  } catch (error) {
    console.error(
      `Error fetching designer event upload poster files for request ${posterRequestId}:`,
      error
    );
    throw new Error(
      getErrorMessage(error, "Failed to fetch event upload poster files")
    );
  }
};

/**
 * Designer side — Delete a social media upload by upload_id
 * DELETE /event_uploads/designer/delete-social-media-uploads/{upload_id}
 */
export const deleteDesignerSocialMediaUpload = async (uploadId) => {
  if (!uploadId) return null;
  try {
    const res = await api.delete(
      `/event_uploads/designer/delete-social-media-uploads/${uploadId}`
    );
    return res.data;
  } catch (error) {
    console.error(`Error deleting designer social media upload ${uploadId}:`, error);
    throw new Error(
      getErrorMessage(error, "Failed to delete uploaded file")
    );
  }
};


/* ==========================================================================
   ADMIN SIDE — SOCIAL MEDIA UPLOAD APPROVAL SERVICES
   ========================================================================== */

/**
 * Admin side — List all SocialMediaUploadRequests submitted by designers
 * GET /event_uploads/admin/social-media-upload-requests/{event_type}?search=<search>&status=<status>&page=<page>&page_size=<page_size>
 */
export const fetchAdminSocialMediaUploadRequests = async ({
  eventType = "fdp",
  search = "",
  status = null,
  page = 1,
  page_size = 10
} = {}) => {
  try {
    const params = { page, page_size };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    if (status && status !== "all") {
      params.status = status;
    }
    const normalizedType = String(eventType || "fdp").toLowerCase().includes("tech")
      ? "tech_camp"
      : "fdp";
    const res = await api.get(
      `/event_uploads/admin/social-media-upload-requests/${normalizedType}`,
      { params }
    );
    return (
      res.data || {
        page: 1,
        page_size: 10,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
        data: []
      }
    );
  } catch (error) {
    console.error("Error fetching admin social media upload requests:", error);
    throw new Error(
      getErrorMessage(error, "Failed to fetch social media upload requests")
    );
  }
};

/**
 * Admin side — Get all active files for a specific social-media upload request
 * GET /event_uploads/admin/social-media-upload-requests/{request_id}/files
 */
export const fetchAdminSocialMediaUploadFiles = async (requestId) => {
  if (!requestId) return { request_id: null, bookslot_id: null, status: null, files: [] };
  try {
    const res = await api.get(
      `/event_uploads/admin/social-media-upload-requests/${requestId}/files`
    );
    return res.data || { request_id: requestId, files: [] };
  } catch (error) {
    console.error(
      `Error fetching admin social media upload files for request ${requestId}:`,
      error
    );
    throw new Error(
      getErrorMessage(error, "Failed to fetch social media upload files")
    );
  }
};

/**
 * Admin side — Approve or reject a designer's social-media upload request
 * PATCH /event_uploads/admin/social-media-upload-requests/{request_id}/review
 * Body: { "action": "approved" } or { "action": "rejected", "reason": "..." }
 */
export const reviewAdminSocialMediaUploadRequest = async (requestId, payload) => {
  try {
    const res = await api.patch(
      `/event_uploads/admin/social-media-upload-requests/${requestId}/review`,
      payload
    );
    return res.data;
  } catch (error) {
    console.error(
      `Error reviewing admin social media upload request ${requestId}:`,
      error
    );
    throw new Error(
      getErrorMessage(error, "Failed to review social media upload request")
    );
  }
};


